/**
 * The `pagamento` block, built in one place for every screen that produces one.
 *
 * Two screens send it — the creation form and the draft editor — and they must
 * send the *same object*: the draft's block is what promotion hands to the XML
 * generator and to the scadenzario seeding, so a draft whose payment terms were
 * shaped even slightly differently would be issued as a different document than
 * the identical invoice typed straight into `InvoiceCreate.svelte`. Two editors
 * that have to agree forever is the duplication this module exists to prevent;
 * `lib/totals.js` exists for the same reason on the money side.
 *
 * Kept free of Svelte imports so it can be unit-tested directly.
 */

/** What the form offers when nothing else says otherwise: bonifico a 30 giorni. */
export const PAYMENT_DEFAULT_DAYS = 30;

/** A blank set of terms, in the shape the editor binds to. */
export function emptyPaymentTerms() {
  return {
    condizioni: 'TP02',
    modalitaPagamento: 'MP05',
    iban: '',
    giorni: PAYMENT_DEFAULT_DAYS,
    // A date the document already carried (inherited), or the one chosen with
    // «Scadenza fissa» on a draft. See paymentTermsFromStored().
    dataScadenza: '',
    // Whether the operator states the due date as a fixed day rather than in
    // days from the invoice date. Only ever offered on a draft.
    fixedMode: false,
    // The stored block these terms were read out of, or null on a document
    // that never had one. Never bound to an input: it is what carries the rest
    // of the block back out again. See paymentTermsFromStored().
    stored: null,
  };
}

/**
 * Whether a stored `pagamento` block describes a plan in instalments.
 *
 * A plan is the shape the four inputs cannot say: several amounts, each on its
 * own day. Every screen that collects payment terms has to recognise it and
 * decline to rewrite it, rather than flattening it into the one payment it can
 * express — see `buildPagamento()`.
 *
 * A single-element `dettagli` counts too. It still carries per-instalment
 * fields — its own ModalitaPagamento, its own DataScadenzaPagamento — that the
 * flat shape has nowhere to put.
 *
 * @param {object|null|undefined} pagamento - the stored block
 * @returns {boolean}
 */
export function hasInstalmentPlan(pagamento) {
  return Array.isArray(pagamento?.dettagli) && pagamento.dettagli.length > 0;
}

/**
 * The instalments behind a set of editor terms, or null when there are none.
 *
 * The editor shows them read-only: an operator who opened a three-instalment
 * draft to fix the causale has to be able to see the plan and be told why it
 * is not editable here.
 *
 * @param {object|null|undefined} terms - the editor's fields, as read back by
 *   `paymentTermsFromStored()`
 * @returns {Array<object>|null}
 */
export function instalmentPlanOf(terms) {
  return hasInstalmentPlan(terms?.stored) ? terms.stored.dettagli : null;
}

/**
 * Whether the company's default payment-method preset may be applied now.
 *
 * Asked every time `PaymentTermsEditor` loads the preset list — which is every
 * time it *mounts*, and the creation wizard unmounts it on every step change:
 * step 2 is the payment step and step 3 is the summary, so 2 → 3 → 2 is the
 * ordinary review path. The guard therefore has to answer "no" for a document
 * the operator has already decided about, not merely for one that currently
 * holds a preset.
 *
 * `presetChosen` is what «no preset chosen yet» and «the operator chose
 * Inserimento manuale» differ by, and it is the whole fix: both leave `method`
 * null, and on remount the second one was being overwritten with the house
 * preset — over the ModalitaPagamento, CondizioniPagamento, IBAN and giorni
 * that had just been typed by hand. It lives in the parent, beside `method`,
 * because the component that asks the question is the one that disappears.
 *
 * @param {object} args
 * @param {boolean} args.autoApplyDefault - false when the document states terms
 *   of its own (a draft prepared through the API, an invoice being corrected)
 * @param {object|null} args.method - the preset currently applied, if any
 * @param {boolean} args.presetChosen - whether a preset decision has already
 *   been taken on this document: the operator using the picker, «Inserimento
 *   manuale» included, or the default having been applied once already
 * @param {object|null} [args.terms] - the editor's fields, for the stored plan
 * @returns {boolean}
 */
export function shouldApplyDefaultPreset({
  autoApplyDefault, method, presetChosen, terms = null,
}) {
  if (!autoApplyDefault || method || presetChosen) return false;
  // A preset restates ModalitaPagamento, CondizioniPagamento, IBAN and the
  // terms in days — none of which describe an instalment plan.
  return !hasInstalmentPlan(terms?.stored);
}

/**
 * Read a stored `metadata.pagamento` back into the editor's shape.
 *
 * A document that stored no payment block at all is not the same as one that
 * stored an empty one: the first gets the house defaults, the second is what
 * the operator deliberately left. `null` therefore means "nothing was ever
 * chosen here" and returns the defaults, exactly as the creation form starts.
 *
 * `dataScadenza` is carried back so that a draft prepared through the API — by
 * an ERP, by an agent, by the connector — that states an absolute due date
 * keeps it when a human opens the draft and saves it. A date stated with no
 * days is a **fixed** due date and opens the editor in «Scadenza fissa»; a date
 * that arrives beside days (the pair the dated creation form writes, a computed
 * date and the days it came from) is shown with a notice and the offer to
 * replace it with terms in days. Either way it is never discarded on its own.
 *
 * The same argument holds for the whole of the rest of the block, and that is
 * what `stored` is. FatturaPA's DatiPagamento says far more than these five
 * fields: the beneficiary, the bank and its ABI/CAB/BIC, the day the terms run
 * from, and `dettagli` — a plan in instalments. A screen that rebuilt the block
 * out of the five fields it happens to show would delete every one of them on
 * the first save, which is what it did: an operator opening a three-instalment
 * draft to fix the causale reissued it as one payment of the full amount, with
 * no due date and no bank, and seeded one undated tranche the scadenzario
 * cannot place on a calendar. `buildPagamento()` spreads `stored` back and
 * overwrites only what the editor actually presents.
 */
export function paymentTermsFromStored(pagamento) {
  if (!pagamento) return emptyPaymentTerms();
  return {
    condizioni: pagamento.condizioni || 'TP02',
    // The one field that must survive as empty: '' is «nessuna modalità», and
    // it is what makes a document carry no DatiPagamento at all.
    modalitaPagamento: pagamento.modalitaPagamento ?? '',
    iban: pagamento.iban || '',
    giorni: pagamento.giorniTerminiPagamento ?? '',
    dataScadenza: pagamento.dataScadenza ? String(pagamento.dataScadenza).slice(0, 10) : '',
    fixedMode: Boolean(pagamento.dataScadenza) && (pagamento.giorniTerminiPagamento == null),
    // Verbatim, never copied field by field: what this editor does not know
    // about is exactly what it must not touch.
    stored: pagamento,
  };
}

/**
 * GiorniTerminiPagamento as the XSD admits it: a plain non-negative integer.
 *
 * `null` for anything else — an emptied field, a stray letter — so the caller
 * can tell "no term stated" from a stated zero. Zero is a real answer
 * (pagamento immediato) and is not the same as leaving the field blank.
 *
 * @param {string|number|null|undefined} value
 * @returns {number|null}
 */
export function normalizeDueDays(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/**
 * The due date those terms fall on, counted from the day the document carries.
 *
 * Calendar arithmetic in UTC on the `YYYY-MM-DD` the caller passes, never
 * `new Date()` plus local hours: `DataScadenzaPagamento` is a day, and a due
 * date computed through a local-time Date lands on the previous day for every
 * operator east of Greenwich once the clock crosses midnight.
 *
 * @param {string|Date|null} anchorDate - the invoice date, `YYYY-MM-DD`
 * @param {string|number|null} giorni - the agreed terms in days
 * @returns {string|null} `YYYY-MM-DD`, or null when either half is missing
 */
export function dueDateFromDays(anchorDate, giorni) {
  const days = normalizeDueDays(giorni);
  if (days === null || !anchorDate) return null;
  const anchor = anchorDate instanceof Date
    ? (Number.isNaN(anchorDate.getTime()) ? '' : anchorDate.toISOString().slice(0, 10))
    : String(anchorDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(anchor)) return null;
  const [year, month, day] = anchor.split('-').map(Number);
  const due = new Date(Date.UTC(year, month - 1, day + days));
  return Number.isNaN(due.getTime()) ? null : due.toISOString().slice(0, 10);
}

/**
 * Today, as the calendar the operator lives in reads it.
 *
 * `new Date().toISOString()` reads UTC, and Italy runs an hour or two ahead of
 * it: between local midnight and 01:00 or 02:00 that is *yesterday*. The
 * backend already truncates a defaulted invoice date to the local calendar day
 * (`defaultInvoiceDate()`), so a screen that wants to show what issuing "today"
 * would give has to agree with it — otherwise the preview of a due date lands a
 * day before the one the document will actually carry.
 *
 * @returns {string} `YYYY-MM-DD`
 */
export function todayCalendarDay() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * The `pagamento` body both screens send.
 *
 * `anchorDate` is the whole difference between them. An invoice being created
 * has its date on screen, so the absolute `DataScadenzaPagamento` is computed
 * here and travels with the document. **A draft has no date at all** — it gets
 * one only at promotion, possibly weeks later — so it passes `null` and states
 * its terms the other way FatturaPA allows: `GiorniTerminiPagamento`, counted
 * from the invoice date by whoever finally issues it. An absolute date chosen
 * against no anchor is not a term, it is a guess that will not move.
 *
 * **Only the fields the editor presents are ever overwritten.** Everything else
 * the stored block held — the beneficiary, the bank, ABI/CAB/BIC,
 * DataRiferimentoTermini — is spread back out of `terms.stored`, because a
 * screen that shows five fields has no grounds to delete the other six. And a
 * block that carries `dettagli` is handed back untouched: a plan in instalments
 * is more than these inputs can say, so it is shown read-only rather than
 * flattened into the single payment they can express. That flattening reissued
 * a three-instalment draft as one payment of the full amount under a
 * CondizioniPagamento that had stopped being true, and seeded one undated
 * tranche — which `findDueByCompany()` excludes, so the invoice went out and
 * never reached the scadenzario at all.
 *
 * `modalitaPagamento: null` is what «nessuna modalità» produces — a block that
 * exists and states that this document names no payment method. `resolvePaymentPlan()`
 * reads it as no DatiPagamento and no tranches, exactly as a missing block, but
 * the two are no longer indistinguishable on reload: a stored `null` was read
 * back as "nothing was ever chosen here" and the company's default preset was
 * then applied straight over the choice the operator had deliberately made.
 *
 * @param {object} args
 * @param {object} args.terms - the editor's fields, plus the `stored` remainder
 * @param {string} args.importoPagamento - what this payment is for, as a decimal string
 * @param {string|Date|null} [args.anchorDate] - the invoice date, when there is one
 * @param {object|null} [args.method] - the saved payment-method preset in use
 * @returns {object} the block to store, never null
 */
export function buildPagamento({ terms, importoPagamento, anchorDate = null, method = null }) {
  const t = terms || {};
  const stored = t.stored || null;
  // Returned as it came in, by reference: there is nothing on this screen that
  // could have changed it, and rebuilding it is the failure itself.
  if (hasInstalmentPlan(stored)) return stored;

  // A chosen preset restates the coordinates in full — that is what choosing
  // one means, and an empty field on it is a deliberate blank. With no preset
  // in play the stored block keeps whatever it already carried.
  const coordinate = (key) => (method ? (method[key] || null) : (stored?.[key] ?? null));

  // A fixed due date, chosen on a draft. It is sent under the name the API
  // takes for a date the operator fixed on purpose, and the days are cleared:
  // an instalment states its due date one way or the other, never both. With
  // no date typed yet the draft simply states its terms in days.
  if (!anchorDate && t.fixedMode && t.dataScadenza) {
    return {
      ...(stored || {}),
      condizioni: t.condizioni || 'TP02',
      modalitaPagamento: t.modalitaPagamento || null,
      importoPagamento,
      iban: t.iban || null,
      dataScadenza: null,
      dataScadenzaPagamento: t.dataScadenza,
      giorniTerminiPagamento: null,
      beneficiario: coordinate('beneficiario'),
      istitutoFinanziario: coordinate('istitutoFinanziario'),
      abi: coordinate('abi'),
      cab: coordinate('cab'),
      bic: coordinate('bic'),
    };
  }

  const days = normalizeDueDays(t.giorni);
  return {
    // The rest of the stored block first, so the named fields below overwrite
    // it and nothing else does.
    ...(stored || {}),
    condizioni: t.condizioni || 'TP02',
    modalitaPagamento: t.modalitaPagamento || null,
    importoPagamento,
    iban: t.iban || null,
    // Blank terms still produce the house 30 days on a dated document, which is
    // what the form has always sent; a stated 0 is honoured as 0 rather than
    // silently becoming a month, which is a due date nobody agreed to.
    //
    // With no anchor — a draft — nothing can be computed, and an absolute date
    // the document already carried is kept rather than dropped. The terms in
    // days still travel beside it: they are what the recipient reads if the
    // date is later replaced, and what the scadenzario counts from.
    dataScadenza: dueDateFromDays(anchorDate, days === null ? PAYMENT_DEFAULT_DAYS : days)
      || t.dataScadenza || null,
    giorniTerminiPagamento: days,
    // The preset carries fields the four inputs have no room for, and they
    // belong in DettaglioPagamento rather than in the settings screen alone.
    // With no preset chosen they are the stored block's own — this screen never
    // showed them, so it has nothing to say about them.
    beneficiario: coordinate('beneficiario'),
    istitutoFinanziario: coordinate('istitutoFinanziario'),
    abi: coordinate('abi'),
    cab: coordinate('cab'),
    bic: coordinate('bic'),
  };
}
